import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

// Small shared presentational bits for the technical mobile dashboard pages — kept in one
// file since each piece is a few lines and used across 6+ pages (stat cards, alarm/signal
// badges, a generic progress meter). Status colors intentionally fixed (not themed), same
// convention as TechnicalConnectionsPage's OnlineMeter.

// The one card surface for the whole technical app view. Every framed block on these
// screens (health hero, bandwidth, alarms, …) spreads this instead of repeating its own
// border/radius, so the shell has a single elevation language and one place to change it.
//
// Lift comes from a soft shadow rather than a grey outline: on the tinted app background a
// hairline border is what makes a stack of cards read as a spreadsheet, while the shadow
// separates them and still leaves white as the only "content" colour. The border that
// remains is near-invisible and only there to keep the card edge crisp on the low-contrast
// screens these phones ship with.
export const CARD = {
  background: 'var(--cb-ffffff)',
  border: '1px solid var(--cb-eceef5)',
  borderRadius: 18,
  boxShadow: '0 1px 2px rgba(16,24,40,.04), 0 8px 20px -12px rgba(16,24,40,.16)',
};

// Wraps a tile in whatever navigation it was given, keeping every tile the same flex item
// regardless of whether it links internally, externally, or nowhere.
function TileLink({ to, href, children, style }) {
  const wrapper = { textDecoration: 'none', color: 'inherit', display: 'flex', ...style };
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" style={wrapper}>{children}</a>;
  if (to) return <Link to={to} style={wrapper}>{children}</Link>;
  return <div style={wrapper}>{children}</div>;
}

// Tinted status tile — icon chip and label on the top row, the figure underneath, and a
// one-word verdict below it in the tile's own color. The verdict line is the point: "Alarms
// 40" says nothing a technician can act on, "Alarms 40 / Need attention" does, and the tint
// carries the same reading across the row without anyone having to read all four.
//
// `color` must be a hex string — the tint and icon chip are derived from it with an alpha
// suffix, which a `var(--…)` value cannot produce.
export function StatCard({ icon: Icon, value, label, sub, to, href, color = '#1e40af' }) {
  // width:'100%' is the fix that matters: the wrapper is itself a flex container (needed so
  // height:100% has something definite to resolve against), which means the tile — its one
  // child — defaults to a content-sized flex item along the main axis instead of filling the
  // slot. Without this every tile sized itself to its own label/value text length.
  //
  // flex-basis is a quarter, not a half — all four of these (OLTs/Alarms/Open Tickets/Uplink)
  // sit in one row now instead of wrapping to 2x2, so the icon/type sizes below are tuned
  // tight enough (26px chip, compact type) to still read at ~85px wide on a phone.
  return (
    <TileLink to={to} href={href} style={{ flex: '1 1 calc(25% - 9px)', minWidth: 0 }}>
      <div style={{
        width: '100%', height: '100%', boxSizing: 'border-box',
        background: `${color}12`, border: `1px solid ${color}24`, borderRadius: 14,
        padding: '10px 9px 11px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
          {Icon && (
            <div style={{
              width: 26, height: 26, borderRadius: 8, flexShrink: 0, background: `${color}1f`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={14} color={color} />
            </div>
          )}
          <span style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--c-4b5563)', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        </div>
        <div style={{ fontSize: '1.25rem', fontWeight: 800, lineHeight: 1, color: 'var(--c-111827)', letterSpacing: '-.02em' }}>
          {value}
        </div>
        {sub && (
          <div style={{ fontSize: '.63rem', fontWeight: 600, color, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
        )}
      </div>
    </TileLink>
  );
}

// Wide tinted row — same tint language as StatCard but laid out for a destination rather
// than a reading: icon chip, the figure with its caption, and a chevron saying it opens
// something.
export function ActionCard({ icon: Icon, label, value, sub, to, href, color = '#1e40af' }) {
  return (
    <TileLink to={to} href={href} style={{ flex: '1 1 calc(50% - 7px)', minWidth: 150 }}>
      <div style={{
        width: '100%', boxSizing: 'border-box', minHeight: 76,
        background: `${color}12`, border: `1px solid ${color}24`, borderRadius: 16,
        padding: '13px 12px 13px 13px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        {Icon && (
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: `${color}1f`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={20} color={color} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--c-6b7280)' }}>{label}</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--c-111827)', lineHeight: 1.15 }}>{value}</div>
          {sub && (
            <div style={{ fontSize: '.7rem', color: 'var(--c-6b7280)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
          )}
        </div>
        <ChevronRight size={18} color="#9ca3af" style={{ flexShrink: 0 }} />
      </div>
    </TileLink>
  );
}

export const SEVERITY_COLOR = {
  critical: { color: 'var(--c-991b1b)', bg: 'var(--cb-fee2e2)', label: 'Critical' },
  major:    { color: 'var(--c-92400e)', bg: 'var(--cb-fef3c7)', label: 'Major' },
  minor:    { color: 'var(--c-1e40af)', bg: 'var(--cb-dbeafe)', label: 'Minor' },
};

export function SeverityBadge({ severity }) {
  const s = SEVERITY_COLOR[severity] || SEVERITY_COLOR.minor;
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
}

// Same dBm thresholds/colors as OLTPage's SignalBadge — kept in sync deliberately.
export function SignalBadge({ dbm }) {
  if (dbm == null || dbm === '') return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const val = parseFloat(dbm);
  let color = 'var(--c-065f46)', bg = 'var(--cb-d1fae5)';
  if (val < -27) { color = 'var(--c-991b1b)'; bg = 'var(--cb-fee2e2)'; }
  else if (val < -24) { color = 'var(--c-92400e)'; bg = 'var(--cb-fef3c7)'; }
  return <span className="badge" style={{ background: bg, color }}>{val} dBm</span>;
}

export function StatusDot({ online }) {
  return (
    <span style={{
      width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 6,
      background: online ? 'var(--cb-0ca30c)' : 'var(--cb-d03b3b)',
    }} />
  );
}

// Generic labelled progress bar — pct-based severity color when `severityByPct` is set
// (used for utilization/health meters), otherwise a fixed `color`.
export function MeterBar({ pct, color, severityByPct, height = 8 }) {
  const resolved = severityByPct
    ? (pct >= 90 ? '#d03b3b' : pct >= 75 ? '#fab219' : '#0ca30c')
    : (color || 'var(--primary)');
  return (
    <div style={{ height, borderRadius: height / 2, background: `${resolved}22`, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, background: resolved, borderRadius: height / 2, transition: 'width .3s' }} />
    </div>
  );
}

export function EmptyState({ icon: Icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
      {Icon && <Icon size={28} style={{ marginBottom: 8, opacity: .5 }} />}
      <div style={{ fontSize: '.9rem' }}>{text}</div>
    </div>
  );
}

export function LoadingBlock() {
  return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
}
